import { useState, useRef, useEffect } from 'react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useReactiveVar } from '@apollo/client'
import { AccountCircle } from '@mui/icons-material'
import {
  Box,
  IconButton,
  Avatar,
  Popover,
  Grid,
  Divider,
  Button,
  Typography,
} from '@mui/material'
import * as Sentry from '@sentry/nextjs'
import Cookies from 'universal-cookie'
import { COOKIE_NAME, SALESFORCE_COOKIE } from '../../const/cookie'
import { hasTermResourceKeyword, PTN } from '../../lib/access-validator'
import { hasPaidMembershipVar } from '../../lib/apollo-client/cache'
import useUserAccount from '../../lib/hooks/useUserAccount'
import { hostnameForCookie } from '../../lib/utils'
import paths from '../../paths/path'
import CtaButton from '../interactives/Buttons/CtaButton'

const cookies = new Cookies()

const DashboardAnnotations = dynamic(
  () => import('../AnnotationComponents/DashboardAnnotations'),
  {
    ssr: false,
  }
)

const UserAccountToolbarMenu = ({ mobile }) => {
  const { user, userAccountUser, userAccountAccess } = useUserAccount()
  const hasPaidMembership = useReactiveVar(hasPaidMembershipVar)
  const iconBtnRef = useRef(null)
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [notesOpen, setNotesOpen] = useState(false)
  const [hasAccess, setHasAccess] = useState(false)

  const toggleAnnotationsDrawerHandler = (open) => {
    setPopoverOpen(false)
    setNotesOpen(open)
  }

  const logoutOutClickHandler = () => {
    setPopoverOpen(false)

    // Clear Sentry user context before logout
    Sentry.setUser(null)
    Sentry.setTags({
      authenticated: 'false',
      userType: 'anonymous',
    })

    cookies.remove(COOKIE_NAME, {
      path: '/',
      domain: hostnameForCookie,
    })
    cookies.remove(SALESFORCE_COOKIE, {
      path: '/',
      domain: hostnameForCookie,
    })
    // Redirect to Salesforce logout URL
    window.location.href =
      process.env.NEXT_PUBLIC_SALESFORCE_BACKEND_LOGOUT_URL_TEST
  }

  const handleLoginClick = () => {
    const returnUrl = window.location.href
    window.location.href = `${process.env.NEXT_PUBLIC_SALESFORCE_BACKEND_URL}?returnUrl=${returnUrl}`
    sessionStorage.setItem('returnUrl', returnUrl)
  }

  useEffect(() => {
    if (user?.authenticated) {
      const checkUserHasAccess = async () => {
        try {
          const hasAccess = await hasTermResourceKeyword(
            [
              PTN.SELECT_SUBSCRIPTION_KEYWORD,
              PTN.PREMIUM_SUBSCRIPTION_KEYWORD,
              PTN.DIGITAL_BASIC_ANNUAL,
              PTN.STANDARD_SELECT_MONTHLY,
              PTN.PAID_SUBSCRIPTIONS_KEYWORDS,
              PTN.PAYWALL_MEMBERSHIP_KEYWORDS,
              PTN.ELBADGE_KEYWORDS,
              PTN.MEMBER_BADGE_KEYWORDS,
            ],
            userAccountAccess
          )
          setHasAccess(hasAccess)
        } catch (error) {
          console.error('Error checking user access:', error)
        }
      }

      checkUserHasAccess()
    }
  }, [user?.authenticated, userAccountAccess])

  return user?.authenticated ? (
    <>
      <Box ml={[2, 0]}>
        <IconButton
          aria-label='profile button'
          component='span'
          ref={iconBtnRef}
          onClick={() => setPopoverOpen(true)}
          data-testid='profile-button'
        >
          <Avatar
            alt={user?.user?.email || 'logo'}
            src={user?.user?.photo || '/static/images/avatar/3.jpg'}
            sx={{
              width: 32,
              height: 32,
              border: '2px solid white',
              boxShadow: 3,
            }}
          />
        </IconButton>
      </Box>
      {!mobile && !hasPaidMembership && (
        <Box ml={3}>
          <CtaButton
            variant='contained'
            color='primary'
            label='Upgrade'
            href={paths.subscribe}
          />
        </Box>
      )}
      <Popover
        id='header-profile'
        open={popoverOpen}
        anchorEl={iconBtnRef?.current}
        onClose={() => setPopoverOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Grid
          container
          sx={{
            width: 300,
            borderRadius: '8px',
          }}
          spacing={2}
        >
          <Grid item xs={12}>
            <Box display='flex' alignItems='center' pt={2} px={2}>
              <AccountCircle />
              <Box ml={2}>
                <Typography
                  variant='h6'
                  data-testid='account-username'
                  textTransform='capitalize'
                >
                  {userAccountUser?.salesforceAccess?.user?.firstName +
                    ' ' +
                    userAccountUser?.salesforceAccess?.user?.lastName}
                </Typography>
                {userAccountUser?.access && (
                  <Typography variant='subtitle3'>
                    {userAccountUser?.access?.filter(
                      (item) => item.term.termName
                    )}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <Box px={2}>
              <Button
                sx={{
                  opacity: 0.6,
                  '&:hover': {
                    textDecoration: 'none',
                  },
                  '& a': {
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'none',
                    },
                  },
                }}
              >
                <Link href={process.env.NEXT_PUBLIC_SALESFORCE_DASHBOARD_URL}>
                  <a>Dashboard</a>
                </Link>
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box px={2}>
              <Button
                // startIcon={<PermIdentity />}
                sx={{
                  opacity: 0.6,
                  '&:hover': {
                    textDecoration: 'none',
                  },
                  '& a': {
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'none',
                    },
                  },
                }}
              >
                <Link
                  href={
                    process.env.NEXT_PUBLIC_SALESFORCE_BACKEND_EDIT_URL_TEST
                  }
                >
                  <a>Account Details</a>
                </Link>
              </Button>
            </Box>
          </Grid>
          {hasAccess && (
            <Grid item xs={12}>
              <Box px={2}>
                <Button
                  // startIcon={<History />}
                  sx={{
                    opacity: 0.6,
                    '&:hover': {
                      textDecoration: 'none',
                    },
                    '& a': {
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'none',
                      },
                    },
                  }}
                >
                  <Link href='/ascd-central'>
                    <a>Member Benefits</a>
                  </Link>
                </Button>
              </Box>
            </Grid>
          )}
          <Grid item xs={12}>
            <Box px={2}>
              <Button
                // startIcon={<History />}
                sx={{
                  opacity: 0.6,
                  '&:hover': {
                    textDecoration: 'none',
                  },
                  '& a': {
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'none',
                    },
                  },
                }}
              >
                <Link
                  href={process.env.NEXT_PUBLIC_SALESFORCE_MEMBER_BENEFITS_URL}
                >
                  <a>Downloads</a>
                </Link>
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box px={2}>
              <Button
                // startIcon={<History />}
                sx={{
                  opacity: 0.6,
                  '&:hover': {
                    textDecoration: 'none',
                  },
                  '& a': {
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'none',
                    },
                  },
                }}
              >
                <Link
                  href={process.env.NEXT_PUBLIC_SALESFORCE_BACKEND_BOOK_HISTORY}
                >
                  <a>Book Order History</a>
                </Link>
              </Button>
            </Box>
          </Grid>
          {/* Notes & Highlights are being commented out in the case that we need to bring it back in the future
          <Grid item xs={12}>
            <Box px={2}>
              <Button
                sx={{
                  opacity: 0.6,
                  '&:hover': {
                    textDecoration: 'none',
                  },
                  '& a': {
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'none',
                    },
                  },
                }}
                // startIcon={<FormatQuote />}
                onClick={() => toggleAnnotationsDrawerHandler(true)}
              >
                Notes & Highlights
              </Button>
            </Box>
          </Grid> */}
          <Grid item xs={12}>
            <Box px={2}>
              <Button
                // startIcon={<Password />}
                sx={{
                  opacity: 0.6,
                  '&:hover': {
                    textDecoration: 'none',
                  },
                  '& a': {
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'none',
                    },
                  },
                }}
                data-testid='account-reset-password'
              >
                <Link
                  href={
                    process.env
                      .NEXT_PUBLIC_SALESFORCE_BACKEND_RESET_PASSWORD_URL_TEST
                  }
                >
                  <a>Reset Password</a>
                </Link>
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <Box px={2} mb={2}>
              <Button
                sx={{
                  opacity: 0.6,
                }}
                // startIcon={
                //   <ExitToApp style={{ transform: 'rotate(-180deg)' }} />
                // }
                onClick={() => logoutOutClickHandler()}
                data-testid='account-sign-out'
              >
                Sign Out
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Popover>
      <DashboardAnnotations
        userId={userAccountUser?.masterCustomerId}
        open={notesOpen}
        toggleOpen={(open) => toggleAnnotationsDrawerHandler(open)}
      />
    </>
  ) : (
    <>
      {!mobile && (
        <Box ml={1}>
          <CtaButton
            variant='contained'
            color='primary'
            label='Join ASCD'
            id='subscribe'
            href={paths.subscribe}
          />
        </Box>
      )}
      <Box ml={1}>
        <CtaButton
          testId='login-button'
          variant='outlined'
          color='primary'
          label='Log In'
          onClick={handleLoginClick}
        />
      </Box>
    </>
  )
}

export default UserAccountToolbarMenu
